import { Image as ImageIcon, LayoutDashboard, Maximize, Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ACTIVE_PILL } from './inspector/controls';
import { type StudioMode } from './studioModel';

interface StudioToolbarProps {
  mode: StudioMode;
  onMode: (m: StudioMode) => void;
  zoom: number;
  onZoom: (z: number) => void;
  /** Banner mode — opens the asset library (uploads + placement live there). */
  onOpenAssets: () => void;
}

/**
 * Toolbar above the canvas: mode switch (community wireframe / banner design)
 * and real zoom controls (also on +/-/0). No decorative tools — everything
 * here does what it says.
 */
export function StudioToolbar({ mode, onMode, zoom, onZoom, onOpenAssets }: StudioToolbarProps) {
  return (
    <div className="flex h-11 flex-none items-center justify-between gap-3 border-b border-white/[0.07] bg-ink-900 px-3">
      <div className="flex items-center gap-0.5 rounded-lg border border-white/[0.06] bg-ink-950/70 p-0.5">
        <ModeBtn active={mode === 'community'} onClick={() => onMode('community')} icon={<LayoutDashboard size={13} />}>
          Community
        </ModeBtn>
        <ModeBtn active={mode === 'banner'} onClick={() => onMode('banner')} icon={<ImageIcon size={13} />}>
          Banner
        </ModeBtn>
      </div>

      <div className="flex items-center gap-1">
        {mode === 'banner' && (
          <button
            type="button"
            onClick={onOpenAssets}
            className="mr-1 inline-flex items-center gap-1.5 rounded-md border border-white/[0.1] px-2.5 py-1.5 text-[12px] font-bold text-mid transition-colors hover:border-white/25 hover:text-hi"
          >
            <ImageIcon size={13} strokeWidth={2.2} /> Assets
          </button>
        )}
        <ToolBtn label="Zoom out (-)" onClick={() => onZoom(zoom - 0.1)}>
          <Minus size={14} />
        </ToolBtn>
        <button
          type="button"
          title="Reset zoom (0)"
          onClick={() => onZoom(1)}
          className="min-w-[52px] rounded-md border border-white/[0.08] px-2 py-1 text-center text-[11px] font-bold tabular-nums text-mid transition-colors hover:text-hi"
        >
          {Math.round(zoom * 100)}%
        </button>
        <ToolBtn label="Zoom in (+)" onClick={() => onZoom(zoom + 0.1)}>
          <Plus size={14} />
        </ToolBtn>
        <ToolBtn label="Fit (0)" onClick={() => onZoom(1)}>
          <Maximize size={13} />
        </ToolBtn>
      </div>
    </div>
  );
}

function ModeBtn({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-bold transition-all',
        active ? ACTIVE_PILL : 'text-mid hover:text-hi',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function ToolBtn({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="grid h-7 w-7 place-items-center rounded-md text-mid transition-colors hover:bg-white/10 hover:text-hi"
    >
      {children}
    </button>
  );
}
